import React, { FunctionComponent, ReactNode, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import { Box } from '@mui/material';

// Custom components
import SidebarFeatureCard from '../Map/InfoCards/SidebarFeatureCard';
import GSInfoDialog from '../Map/InfoCards/Dialogs/GSInfoDialog';
import MapCardActions from '@/ui/MapCard/MapCardActions';
import { GridContainer } from '@/ui/Grid';
import Permissions from '@/lib/permissions';
import FormContent from '../FormContent';
import ClassifyButtonGroup from '../Map/InfoCards/LayerFunctionalities/ClassifyButtonGroup';
import FICLocationCompositionButton from '../Map/InfoCards/LayerFunctionalities/FICLocationCompositionButton';
import KuceZaOdmorButton from '../Map/InfoCards/LayerFunctionalities/KuceZaOdmorButton';
import PosjedniciButton from '../Map/InfoCards/LayerFunctionalities/PosjedniciButton';
import Show3DButton from '../Map/InfoCards/LayerFunctionalities/Show3DButton';
import ShowKOButton from '../Map/InfoCards/LayerFunctionalities/ShowKOButton';
import ShowPhotodocButton from '../Map/InfoCards/LayerFunctionalities/ShowPhotodocButton';
import VlasniciButton from '../Map/InfoCards/LayerFunctionalities/VlasniciButton';
import AktivnostiOdrzavanjaButton from '../Map/InfoCards/LayerFunctionalities/AktivnostiOdrzavanjaButton';
import GMKorisniciButton from '../Map/InfoCards/LayerFunctionalities/GMKorisniciButton';
import GMUkopaniButton from '../Map/InfoCards/LayerFunctionalities/GMUkopaniButton';

// Context
import DialogContext from '@/context/DialogContext/DialogContext';
import UserContext, { UserContextType } from '@/context/UserContext/UserContext';

// Types
import { IGFICardOverlayData } from '@/@types/views/map';
import { GFIFeatureType } from '@/@types/services/gsService';
import EnumLayerFunctionality from '@/@types/services/gsServiceEnums';
import { DCRecord } from '@/@types/lib/dataController';
import { IFieldsCollection } from '@/@types/models/model';
import PPZahtjeviButton from '../Map/InfoCards/LayerFunctionalities/PPZahtjeviButton';
import OcijeniButton from '../Map/InfoCards/LayerFunctionalities/OcijeniButton';
import TerPotrosakDokumentiButton from '../Map/InfoCards/LayerFunctionalities/TerPotrosacDokumentiButton';
import getGFIFormConfig from '@/lib/gfiLayout';

type IFeatureInfoPane = {
  featureData: IGFICardOverlayData;
  activeFeatureIndex: any;
  setActiveFeatureIndex: (layer: string, index: number) => void;
  onUndock?: () => void;
};

const FeatureInfoPane: FunctionComponent<IFeatureInfoPane> = ({ featureData, activeFeatureIndex, setActiveFeatureIndex, onUndock }) => {
  const { t } = useTranslation();

  const features = featureData?.features as GFIFeatureType[];
  const { layerData } = featureData;
  const { layer, model, layerFunctionalityId } = layerData;

  const buildFallbackFields = (properties: Record<string, string | number | boolean | null>) =>
    Object.keys(properties).map((key) => ({
      source: key,
      title: key,
      type: typeof properties[key] === 'number' ? 'numeric' : typeof properties[key] === 'boolean' ? 'boolean' : 'text',
    }));

  const fields = useMemo(
    () => (model?.fields?.length ? model.fields : features[0] ? buildFallbackFields(features[0].properties) : []),
    [features, model?.fields]
  );
  const { fieldNames, layout } = useMemo(
    () => getGFIFormConfig(fields, model?.sections, model?.layout),
    [fields, model?.layout, model?.sections]
  );

  const { hideDialog, showDialog } = useContext(DialogContext);
  const userContext = useContext(UserContext) as UserContextType;

  const { hasAnyPermission } = userContext;

  const [dataCache, setDataCache] = useState<Map<number, Map<String, DCRecord[] | DCRecord>>>(
    new Map<number, Map<String, DCRecord[] | DCRecord>>()
  ); // To save requests made by Menu List Items (e.g. Vlasnici, Posjednici fetch)

  const handleAddToCache = (identifier: string, recordId: number, data: DCRecord[] | DCRecord) => {
    setDataCache((prevCache) => {
      const newCache = new Map(prevCache); // Create a new Map from the previous state
      const recordCache = newCache.get(recordId) || new Map<string, DCRecord[]>();
      recordCache.set(identifier, data);
      newCache.set(recordId, recordCache);
      return newCache; // Return the new Map to trigger a state update
    });
  };

  const handleGetFromCache = (identifier: string, recordId: number): DCRecord[] | DCRecord | null => {
    const recordCache = dataCache.get(recordId);
    if (recordCache) {
      return recordCache.get(identifier) || null;
    }
    return null;
  };

  return (
    <Box sx={{ p: 1, gap: 1, display: 'flex', flexDirection: 'column' }}>
      {features.map((feature, index) => {
        const isActive = activeFeatureIndex === index;
        // Prepare a featureData object for this feature
        const singleFeatureData = {
          ...featureData,
          features: [feature],
          record: feature.properties,
        };

        const record = (() => {
          if (featureData) {
            if (featureData.record) {
              return featureData.record;
            }
            if (featureData.features[index]) {
              return featureData.features[index].properties;
            }
            if (featureData.features[0]) {
              return featureData.features[0].properties;
            }
          }
          return null;
        })();

        const modelTitle =
          model?.title_type === 1
            ? t(`${model.title_token}`)
            : model?.title_type === 2 && model?.title_field
            ? record && record[model.title_field]
            : model?.title_type === 3 && model?.title_field
            ? `${t(`${model.title_token}`)}: ${record ? record[model.title_field] : ''}`
            : null;

        const title = model ? modelTitle : record && record.naziv ? record.naziv : layer ? t(`map:layer.${layer}`) : 'Info';

        const handleOpenDialog = (node: ReactNode, identifier: string, hideFormContent: boolean = false) => {
          showDialog(GSInfoDialog, {
            body: node,
            handleClose: () => {
              hideDialog();
            },
            identifier,
            FormContent: !hideFormContent ? GSFormContent : null,
          });
        };

        const GSFormContent = (
          <GridContainer spacing={0} width="100%">
            <FormContent
              fields={fields as IFieldsCollection}
              fieldNames={fieldNames}
              layout={layout}
              columns={1}
              mode="simpleView"
              record={record as DCRecord}
              collapsibleSections
            />
          </GridContainer>
        );

        // Generate custom actions for this feature
        const customActions = (() => {
          const nodes: ReactNode[] = [];
          switch (layerFunctionalityId) {
            case EnumLayerFunctionality.dkp_show_posjednici_vlasnici:
              nodes.push(
                <PosjedniciButton
                  key="posjednici-btn"
                  featureData={singleFeatureData}
                  record={feature.properties}
                  onToggle={handleOpenDialog}
                  addToCache={handleAddToCache}
                  getFromCache={handleGetFromCache}
                />
              );
              nodes.push(
                <VlasniciButton
                  key="vlasnici-btn"
                  featureData={singleFeatureData}
                  record={feature.properties}
                  onToggle={handleOpenDialog}
                  addToCache={handleAddToCache}
                  getFromCache={handleGetFromCache}
                />
              );
              break;
            case EnumLayerFunctionality.dkp_show_posjednici:
              nodes.push(
                <PosjedniciButton
                  key="posjednici-btn"
                  featureData={singleFeatureData}
                  record={feature.properties}
                  onToggle={handleOpenDialog}
                  addToCache={handleAddToCache}
                  getFromCache={handleGetFromCache}
                />
              );
              break;
            case EnumLayerFunctionality.kuce_za_odmor_button:
              nodes.push(
                <KuceZaOdmorButton
                  key="kzo-btn"
                  featureData={singleFeatureData}
                  record={feature.properties}
                  onToggle={handleOpenDialog}
                  addToCache={handleAddToCache}
                  getFromCache={handleGetFromCache}
                  handleCloseMenu={() => {
                    hideDialog();
                  }}
                />
              );
              break;
            case EnumLayerFunctionality.objekti_ko_photodoc:
              nodes.push(<ShowKOButton key="ko-btn" featureData={singleFeatureData} />);
              nodes.push(<ShowPhotodocButton key="photodoc-btn" featureData={singleFeatureData} />);
              break;
            case EnumLayerFunctionality.grobna_mjesta_show_ukopani:
              nodes.push(
                <GMKorisniciButton
                  key="korisnici-btn"
                  featureData={featureData}
                  record={record as DCRecord}
                  onToggle={handleOpenDialog}
                  addToCache={handleAddToCache}
                  getFromCache={handleGetFromCache}
                />
              );
              nodes.push(
                <GMUkopaniButton
                  key="ukopani-btn"
                  featureData={featureData}
                  record={record as DCRecord}
                  onToggle={handleOpenDialog}
                  addToCache={handleAddToCache}
                  getFromCache={handleGetFromCache}
                />
              );
              break;
            case EnumLayerFunctionality.classify:
              nodes.push(<ClassifyButtonGroup key="classify-btns" record={feature.properties} closeInfoCard={() => {}} />); // TODO: implement closeInfoCard
              break;
            case EnumLayerFunctionality.fic_lokacije_show_data:
              nodes.push(
                <FICLocationCompositionButton
                  key="composition-btn"
                  featureData={singleFeatureData}
                  record={feature.properties}
                  onToggle={handleOpenDialog}
                  addToCache={handleAddToCache}
                  getFromCache={handleGetFromCache}
                />
              );
              break;
            case EnumLayerFunctionality.paths_show_activities:
              nodes.push(
                <AktivnostiOdrzavanjaButton
                  key="odrzavanje-btn"
                  featureData={singleFeatureData}
                  record={feature.properties}
                  onToggle={handleOpenDialog}
                  addToCache={handleAddToCache}
                  getFromCache={handleGetFromCache}
                />
              );
              break;
            case EnumLayerFunctionality.pp_zahtjevi_button:
              if (record) {
                nodes.push(<PPZahtjeviButton record={record} />);
              }
              break;
            case EnumLayerFunctionality.prisupacnost_ocijeni_button:
              if (record) {
                nodes.push(<OcijeniButton record={record} />);
              }
              break;
            case EnumLayerFunctionality.ter_potrosac_dokumenti_button:
              nodes.push(
                <TerPotrosakDokumentiButton
                  key="ter-dokumenti-btn"
                  featureData={singleFeatureData}
                  record={feature.properties}
                  onToggle={handleOpenDialog}
                  addToCache={handleAddToCache}
                  getFromCache={handleGetFromCache}
                />
              );
              break;
            default:
              break;
          }
          if (hasAnyPermission([Permissions.ThreeDView]) && layerFunctionalityId !== EnumLayerFunctionality.classify) {
            nodes.push(<Show3DButton key="show-3d-btn" featureData={singleFeatureData} />);
          }
          return nodes;
        })();

        return (
          <SidebarFeatureCard
            key={feature.id || index}
            feature={feature}
            featureData={featureData}
            index={index}
            title={title as string}
            isActive={isActive}
            onClick={(idx) => setActiveFeatureIndex(layer, idx)}
            // fields={fields}
            FormContent={GSFormContent}
            onUndock={onUndock}
          >
            {customActions.length ? (
              <MapCardActions style={{ padding: 0 }}>
                <Box display="flex" flexWrap="wrap" rowGap={0.5} columnGap={0} width="100%" justifyContent="flex-start">
                  {customActions}
                </Box>
              </MapCardActions>
            ) : (
              []
            )}
          </SidebarFeatureCard>
        );
      })}
    </Box>
  );
};

export default FeatureInfoPane;
